<script setup>
import { computed, onMounted, ref } from 'vue'
import { useStore } from 'vuex'
import { ElMessage, ElMessageBox } from 'element-plus'
import { CirclePlus, Delete, Search } from '@element-plus/icons-vue'
import enterpriseInfo from './enterpriseInfo.vue'

const store = useStore()

const activeName = ref('employee')
const searchKeyword = ref('')
const selectedDeptId = ref('')
const employeeList = ref([])
const userInfo = ref({})
const enterpriseDetail = ref({})
const companyTypeOptions = ref([])
const companyTypeDisplayList = ref([])
const typeDialogVisible = ref(false)
const selectedType = ref(null)

const deptTreeData = ref([
  {
    id: 1,
    label: '公司部门',
    isRoot: true,
    children: [
      { id: 11, label: '管理部' },
      { id: 12, label: '销售部' },
      { id: 13, label: '商务部' },
      { id: 14, label: '财务部' },
      { id: 15, label: '采购部' },
      { id: 16, label: '仓储部' },
      { id: 17, label: '物流部' }
    ]
  }
])

const defaultProps = ref({
  label: 'label',
  children: 'children',
  isLeaf: () => false
})

const deptRoleMap = computed(() => {
  const map = {}
  const walk = nodes => {
    ;(nodes || []).forEach(node => {
      if (!node.isRoot) {
        map[String(node.id)] = node.label
      }
      if (node.children?.length) {
        walk(node.children)
      }
    })
  }
  walk(deptTreeData.value)
  return map
})

const findTypePath = (nodes, targetId, path = []) => {
  for (const node of nodes || []) {
    const currentPath = [...path, node.categoryName]
    if (String(node.id) === String(targetId)) {
      return currentPath
    }
    if (node.children?.length) {
      const childPath = findTypePath(node.children, targetId, currentPath)
      if (childPath.length) {
        return childPath
      }
    }
  }
  return []
}

const parseEnterpriseTypeIds = value => {
  if (Array.isArray(value)) {
    return value.map(item => String(item).trim()).filter(Boolean)
  }
  if (value === undefined || value === null || value === '') {
    return []
  }
  return String(value)
    .split(',')
    .map(item => item.trim())
    .filter(Boolean)
}

const getAttachmentIds = value => {
  if (value === undefined || value === null || value === '') {
    return []
  }

  let files = value
  if (typeof value === 'string') {
    try {
      files = JSON.parse(value)
    } catch (error) {
      return value
        .split(',')
        .map(item => item.trim())
        .filter(Boolean)
    }
  }

  const fileList = Array.isArray(files) ? files : [files]
  return fileList
    .map(item => (typeof item === 'object' && item !== null ? item.fileId || item.id || item.uid : item))
    .filter(Boolean)
}

const syncCompanyTypeDisplay = () => {
  const typeIds = parseEnterpriseTypeIds(enterpriseDetail.value.enterpriseType)
  companyTypeDisplayList.value = typeIds.map(typeId => {
    const path = findTypePath(companyTypeOptions.value, typeId)
    return {
      id: typeId,
      name: path.length ? path.join(' / ') : typeId
    }
  })
}

const loadCompanyTypes = async () => {
  const res = await store.dispatch('myMessageModule/enterpriseTreeList', {})
  const { code, data = [] } = res?.data || {}
  if (code === '0') {
    companyTypeOptions.value = Array.isArray(data) ? data : []
    syncCompanyTypeDisplay()
  }
}

const loadEnterpriseInfo = async enterpriseId => {
  const res = await store.dispatch('myMessageModule/getEnterprise', enterpriseId)
  const { code, data = {} } = res?.data || {}
  if (code === '0') {
    enterpriseDetail.value = data
    syncCompanyTypeDisplay()
  }
}

const loadEmployeeList = async enterpriseId => {
  const res = await store.dispatch('myMessageModule/platformUserListByEnterpriseId', enterpriseId)
  const { code, data = [] } = res?.data || {}
  if (code === '0') {
    employeeList.value = Array.isArray(data) ? data : []
  }
}

const filteredEmployeeData = computed(() => {
  const keyword = searchKeyword.value.trim()
  const list = employeeList.value.map((item, index) => ({
    ...item,
    role: deptRoleMap.value[String(item.jobRole)] || item.jobRole || '',
    roleId: item.jobRole === undefined || item.jobRole === null ? '' : String(item.jobRole),
    statusText: item.accountStatus === '1' || item.accountStatus === 1 ? '停用' : item.accountStatus === '0' || item.accountStatus === 0 ? '启用' : '',
    seq: index + 1
  }))
  const deptFilteredList = selectedDeptId.value ? list.filter(item => item.roleId === selectedDeptId.value) : list
  if (!keyword) {
    return deptFilteredList
  }
  return deptFilteredList.filter(item => (item.realName || '').includes(keyword))
})

const handleNodeClick = node => {
  selectedDeptId.value = node?.isRoot ? '' : String(node?.id || '')
}

const handleTransfer = row => {
  ElMessageBox.confirm('是否确认转移公司主账号？', '提示', {
    confirmButtonText: '确认',
    cancelButtonText: '取消',
    type: 'warning'
  })
    .then(async () => {
      const payload = buildSubmitPayload(enterpriseDetail.value.enterpriseType)
      payload.customerId = row.id
      payload.enterpriseId = row.enterpriseId || payload.enterpriseId
      payload.enterpriseName = row.enterpriseName || payload.enterpriseName
      const res = await store.dispatch('myMessageModule/updateCustomerIdForEnterprise', payload)
      if (res?.data?.code === '0') {
        ElMessage.success('转移成功')
        if (userInfo.value.enterpriseId) {
          await loadEmployeeList(userInfo.value.enterpriseId)
        }
      }
    })
    .catch(() => {})
}

const buildCompanyTypeTree = (data, disabled, level = 1) =>
  (data || []).map(item => {
    const current = {
      ...item,
      label: item.categoryName,
      disabled: level === 1 ? true : disabled
    }
    if (item.children?.length) {
      current.children = buildCompanyTypeTree(item.children, disabled, level + 1)
    } else {
      delete current.children
    }
    return current
  })

const buildSubmitPayload = newType => {
  const detail = JSON.parse(JSON.stringify(enterpriseDetail.value))
  return {
    ...detail,
    enterpriseId: detail.id,
    enterpriseType: newType,
    enterpriseLicense: getAttachmentIds(detail.enterpriseLicense)[0] || null,
    sealFlies: getAttachmentIds(detail.sealFlies)[0] || null,
    collectionQrCode: getAttachmentIds(detail.collectionQrCode)[0] || null,
    enterpriseQualification: getAttachmentIds(detail.enterpriseQualification).join(','),
    officeWorkshopEnvironment: getAttachmentIds(detail.officeWorkshopEnvironment).join(',')
  }
}

const handleAddCompanyType = () => {
  selectedType.value = null
  typeDialogVisible.value = true
}

const saveCompanyType = async () => {
  if (!selectedType.value) {
    ElMessage.warning('请选择公司类型')
    return
  }
  const currentTypeIds = parseEnterpriseTypeIds(enterpriseDetail.value.enterpriseType)
  const nextTypeId = String(selectedType.value)
  if (currentTypeIds.includes(nextTypeId)) {
    ElMessage.warning('该公司类型已存在')
    return
  }
  const payload = buildSubmitPayload([...currentTypeIds, nextTypeId].join(','))
  const res = await store.dispatch('myMessageModule/editEnterprise', payload)
  if (res?.data?.code === '0') {
    ElMessage.success('公司类型设置成功')
    typeDialogVisible.value = false
    await loadEnterpriseInfo(userInfo.value.enterpriseId)
  }
}

const handleDeleteSingleCompanyType = typeId => {
  const currentTypeIds = parseEnterpriseTypeIds(enterpriseDetail.value.enterpriseType)
  if (!currentTypeIds.length) {
    ElMessage.warning('当前暂无公司类型可删除')
    return
  }
  const nextTypeIds = currentTypeIds.filter(item => item !== String(typeId))
  if (nextTypeIds.length === currentTypeIds.length) {
    ElMessage.warning('未找到要删除的公司类型')
    return
  }
  ElMessageBox.confirm('是否删除当前公司类型？', '提示', {
    confirmButtonText: '确认',
    cancelButtonText: '取消',
    type: 'warning'
  })
    .then(async () => {
      await removeCompanyType(typeId)
    })
    .catch(() => {})
}

const removeCompanyType = async typeId => {
  const currentTypeIds = parseEnterpriseTypeIds(enterpriseDetail.value.enterpriseType)
  const nextTypeIds = currentTypeIds.filter(item => item !== String(typeId))
  if (nextTypeIds.length === currentTypeIds.length) {
    return
  }
  const payload = buildSubmitPayload(nextTypeIds.length ? nextTypeIds.join(',') : null)
  const res = await store.dispatch('myMessageModule/editEnterprise', payload)
  if (res?.data?.code === '0') {
    ElMessage.success('删除成功')
    await loadEnterpriseInfo(userInfo.value.enterpriseId)
  }
}

const init = async () => {
  const userRes = await store.dispatch('myMessageModule/getUser')
  const { code, data = {} } = userRes?.data || {}
  if (code !== '0') {
    return
  }

  userInfo.value = data
  await Promise.allSettled([
    loadCompanyTypes(),
    data.enterpriseId ? loadEnterpriseInfo(data.enterpriseId) : Promise.resolve(),
    data.enterpriseId ? loadEmployeeList(data.enterpriseId) : Promise.resolve()
  ])
}

onMounted(() => {
  init()
})
</script>

<template>
  <div class="info-setting-page">
    <div class="info-list">
      <el-tabs v-model="activeName" type="card" class="top-tabs">
        <el-tab-pane label="员工管理" name="employee"></el-tab-pane>
      </el-tabs>
      <div class="list-content">
        <div class="search-wrapper">
          <el-input v-model="searchKeyword" placeholder="请输入员工姓名" class="search-input" size="default">
            <template #suffix>
              <el-icon><Search /></el-icon>
            </template>
          </el-input>
        </div>

        <div class="content-layout">
          <div class="dept-sidebar">
            <div class="dept-tree-container">
              <el-tree
                :data="deptTreeData"
                node-key="id"
                default-expand-all
                highlight-current
                :props="defaultProps"
                class="custom-dept-tree"
                @node-click="handleNodeClick"
              />
            </div>
          </div>

          <div class="employee-table-container">
            <el-table
              :data="filteredEmployeeData"
              border
              height="350"
              style="width: 100%"
              :header-cell-style="{ backgroundColor: '#f5f7fa' }"
            >
              <el-table-column prop="seq" label="序号" width="80" align="center" />
              <el-table-column prop="realName" label="姓名" min-width="120" />
              <el-table-column label="角色" min-width="120">
                <template #default="{ row }">
                  <span>{{ row.role || '--' }}</span>
                </template>
              </el-table-column>
              <el-table-column prop="mobilePhone" label="手机号" min-width="180" />
              <el-table-column label="状态" min-width="120" align="center">
                <template #default="{ row }">
                  <span>{{ row.statusText || '--' }}</span>
                </template>
              </el-table-column>
              <el-table-column prop="enterpriseName" label="所属公司名称" min-width="180" />
              <el-table-column label="操作" width="200" align="center">
                <template #default="{ row }">
                  <el-text type="primary" link @click="handleTransfer(row)">转移公司主账号</el-text>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </div>
      </div>
    </div>

    <div class="company-type">
      <div class="section-title">
        <div class="title-content">
          <div class="title-line"></div>
          <span class="title-text">公司类型</span>
          <span class="title-tips">（可按需新增多个类型）</span>
        </div>
        <div class="btn">
          <el-button link size="small" @click="handleAddCompanyType">
            <el-icon><CirclePlus /></el-icon>
            <span>新增</span>
          </el-button>
        </div>
      </div>

      <div class="row">
        <span class="label">公司类型：</span>
        <div v-if="companyTypeDisplayList.length" class="type-list">
          <div v-for="item in companyTypeDisplayList" :key="item.id" class="type-item">
            <span class="type-item__name">{{ item.name }}</span>
            <button type="button" class="type-item__remove" @click="handleDeleteSingleCompanyType(item.id)">
              <span class="type-item__remove-x">x</span>
            </button>
          </div>
        </div>
        <span v-else class="text">--</span>
      </div>
    </div>
  </div>
  <div style="background: #f4f4f4; height: 20px"></div>
  <enterpriseInfo :enterprise-detail="enterpriseDetail" style="margin-top: 20px"></enterpriseInfo>

  <el-dialog v-model="typeDialogVisible" title="选择公司类型" width="1000px" top="10vh" destroy-on-close class="type-select-dialog">
    <div v-if="companyTypeDisplayList.length" class="selected-company-types">
      <span class="selected-company-types__label">已选类型</span>
      <div class="selected-company-types__list">
        <span v-for="item in companyTypeDisplayList" :key="item.id" class="selected-company-types__item">
          {{ item.name }}
          <button type="button" class="selected-company-types__remove" @click="removeCompanyType(item.id)">×</button>
        </span>
      </div>
    </div>
    <div class="type-select-wrapper">
      <div class="type-select-wrapper__label">添加公司类型</div>
      <tree-select
        v-model="selectedType"
        style="width: 100%"
        :clearable="true"
        :options="buildCompanyTypeTree(companyTypeOptions, false)"
        :disable-branch-nodes="true"
        :searchable="true"
        :default-expand-level="0"
        :flatten-search-results="true"
        :open-on-focus="true"
        placeholder="请选择公司类型"
      >
        <template #option-label="{ node }">
          <label class="ct-option">
            <img
              v-if="node.raw.fileUrl"
              :src="node.raw.fileUrl"
              class="ct-option__img"
            />
            <div class="ct-option__body">
              <span class="ct-option__name">{{ node.label }}</span>
              <span v-if="node.raw.intro" class="ct-option__intro">{{ node.raw.intro }}</span>
            </div>
          </label>
        </template>
      </tree-select>
    </div>
    <template #footer>
      <div class="ct-dialog-footer">
        <el-button @click="typeDialogVisible = false" class="ct-btn-cancel">取消</el-button>
        <el-button type="primary" @click="saveCompanyType" class="ct-btn-confirm">确认</el-button>
      </div>
    </template>
  </el-dialog>
</template>

<style lang="less" scoped>
.info-setting-page {
  padding: 20px 0;
  .info-list {
    padding: 0 20px;
    margin-bottom: 30px;
    .top-tabs {
      :deep(.el-tabs__item) {
        height: 30px !important;
        line-height: 30px !important;
        background: #48a09c;
        border-radius: 6px 6px 0 0;
        color: #fff !important;
        font-size: 14px;
      }
      :deep(.el-tabs__item.is-active) {
        background-color: #48a09c;
        color: #ffffff !important;
        border-radius: 6px 6px 0 0;
      }
    }
    .list-content {
      box-shadow: 0px 3px 6px 2px rgba(0, 0, 0, 0.1);
      border-radius: 0px 0px 12px 12px;
      .search-wrapper {
        margin-bottom: 20px;
      }
      .search-input {
        width: 280px;
      }
      .content-layout {
        display: flex;
        gap: 20px;
      }
      .dept-sidebar {
        width: 280px;
        flex-shrink: 0;
      }
      .dept-tree-container {
        width: 280px;
        height: 350px;
        border: 1px solid #e4e7ed;
        border-radius: 8px;
        overflow: hidden;
      }
      :deep(.custom-dept-tree) {
        --el-tree-font-size: 16px;
        --el-tree-node-hover-bg-color: #f5f7fa;
      }
      :deep(.custom-dept-tree .el-tree-node[data-is-root='true'] .el-tree-node__content) {
        background-color: #f5f7fa;
        font-weight: 500;
        height: 44px;
        line-height: 44px;
        border-bottom: 1px solid #e4e7ed;
      }
      :deep(.custom-dept-tree .el-tree-node__content) {
        height: 44px;
        padding: 0 16px !important;
        align-items: center;
        border-bottom: 1px solid #f2f3f5;
      }
      .employee-table-container {
        flex: 1;
        height: 350px;
        border: 1px solid #e4e7ed;
        border-radius: 8px;
        overflow: hidden;
      }
    }
  }

  .company-type {
    padding: 20px 40px;
    .section-title {
      display: flex;
      align-items: center;
      margin-bottom: 24px;
      .title-content {
        display: flex;
        align-items: center;
        gap: 8px;
      }
      .title-line {
        width: 2px;
        height: 18px;
        background-color: #48a09c;
        border-radius: 2px;
      }
      .title-text {
        font-size: 18px;
        font-weight: 600;
        color: #333333;
      }
      .title-tips {
        font-size: 14px;
        color: #989d9d;
      }
    }
    .btn {
      display: flex;
      margin-left: 40px;
      align-items: center;
      :deep(.el-button) {
        color: #48a09c;
        border: none !important;
      }
    }
    .row {
      display: flex;
      align-items: flex-start;
      padding-bottom: 16px;
      border-bottom: 1px solid #eeeeee;
    }
    .label {
      margin-right: 8px;
      color: #666666;
      font-size: 14px;
      line-height: 28px;
      white-space: nowrap;
    }
    .text {
      color: #333333;
      font-size: 14px;
      line-height: 28px;
    }
    .type-list {
      display: flex;
      flex-wrap: wrap;
      gap: 14px;
      flex: 1;
    }
    .type-item {
      display: flex;
      align-items: center;
      gap: 8px;
      min-height: 42px;
      padding: 0 12px 0 14px;
      border: 1px solid #d8ecea;
      border-radius: 999px;
      background: linear-gradient(180deg, #fcfefe 0%, #f2fbfa 100%);
      box-shadow: 0 4px 12px rgba(72, 160, 156, 0.06);
    }
    .type-item__name {
      color: #2f4f4f;
      font-size: 14px;
    }
    .type-item__remove {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 16px;
      height: 16px;
      padding: 0;
      border: none;
      background: transparent;
      color: #9fbab7;
      cursor: pointer;
      appearance: none;
      -webkit-appearance: none;
    }
    .type-item__remove:hover {
      color: #48a09c;
    }
    .type-item__remove:focus,
    .type-item__remove:focus-visible,
    .type-item__remove:active {
      outline: none;
      box-shadow: none;
      background: transparent;
    }
    .type-item__remove-x {
      font-size: 16px;
      line-height: 1;
      font-weight: 300;
    }
  }
}
</style>

<style lang="less">
.type-select-dialog {
  max-width: calc(100vw - 32px);
  border-radius: 10px !important;
  overflow: hidden;
  box-shadow: 0 12px 36px rgba(22, 37, 54, 0.14) !important;

  .el-dialog__body {
    padding: 20px 24px 8px;
  }

  .el-dialog__footer {
    padding: 14px 24px 18px;
    border-top: 1px solid #f0f2f5;
  }
}

.selected-company-types {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  margin-bottom: 18px;
  border: 1px solid #e3eeed;
  border-radius: 8px;
  background: #f7fbfb;
}

.selected-company-types__label {
  flex: 0 0 auto;
  color: #4d6b69;
  font-size: 14px;
  line-height: 28px;
}

.selected-company-types__list {
  display: flex;
  flex: 1;
  flex-wrap: wrap;
  gap: 8px;
}

.selected-company-types__item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  min-height: 28px;
  padding: 0 8px 0 10px;
  color: #397a76;
  font-size: 13px;
  line-height: 1.4;
  border: 1px solid #bfe0dd;
  border-radius: 4px;
  background: #fff;
}

.selected-company-types__remove {
  width: 18px;
  height: 18px;
  padding: 0;
  color: #6e9a97;
  font-size: 17px;
  line-height: 16px;
  border: none;
  background: transparent;
  cursor: pointer;
}

.selected-company-types__remove:hover {
  color: #e16d62;
}

.ct-dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.ct-btn-cancel {
  border-radius: 8px !important;
}

.ct-btn-confirm {
  border-radius: 8px !important;
  background-color: #48a09c !important;
  border-color: #48a09c !important;
}

.type-select-wrapper {
  min-height: 540px;

  .type-select-wrapper__label {
    margin-bottom: 8px;
    color: #303133;
    font-size: 14px;
    font-weight: 500;
  }

  .vue-treeselect__control {
    border-radius: 6px !important;
    border-color: #dcdfe6 !important;
    height: 44px !important;
  }

  .vue-treeselect__menu {
    height: 460px !important;
    max-height: 460px !important;
    margin-top: 6px;
    border-radius: 6px;
    border-color: #dfe7e7;
    box-shadow: 0 6px 18px rgba(35, 74, 71, 0.1);
  }
}

.ct-option {
  display: flex !important;
  align-items: center !important;
  gap: 10px;
  padding: 7px 10px;
  width: 100%;
  box-sizing: border-box;
}

.ct-option__img {
  width: 36px !important;
  height: 36px !important;
  border-radius: 6px;
  object-fit: cover !important;
}

.ct-option__body {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
  flex: 1;
  overflow: hidden;
}

.ct-option__name {
  font-size: 14px;
  color: #303133;
}

.ct-option__intro {
  font-size: 12px;
  color: #909399;
}
</style>
